import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { currentMonth, todayISO, fmtDate, fmtTime } from "../lib/format";
import { PageHeader, StatusBadge, EmptyState, StatCard } from "../components/Common";
import { CheckInWidget } from "../components/CheckInWidget";
import { AttendanceUpload } from "../components/AttendanceUpload";
import { UpcomingHolidays } from "../components/UpcomingHolidays";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MyMonth = ({ tick, entityId }) => {
  const [month, setMonth] = useState(currentMonth());
  const [recs, setRecs] = useState([]);
  const [hols, setHols] = useState([]);
  useEffect(() => {
    api.get("/attendance", { params: { month } }).then((r) => setRecs(r.data));
    if (/^\d{4}-\d{2}$/.test(month)) api.get("/holidays", { params: { year: month.slice(0, 4), entity_id: entityId } }).then((r) => setHols(r.data.filter((h) => h.date.startsWith(month))));
  }, [month, tick, entityId]);
  const hours = recs.reduce((s, r) => s + (r.hours || 0), 0).toFixed(1);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48 bg-white" data-testid="attendance-month-input" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Days present" value={recs.filter((r) => r.status !== "absent").length} testid="att-days" />
        <StatCard label="Late arrivals" value={recs.filter((r) => r.status === "late").length} testid="att-late" />
        <StatCard label="Hours" value={hours} testid="att-hours" />
        <StatCard label="Holidays" value={hols.length} testid="att-holidays" hint={hols.map((h) => h.name).join(", ") || undefined} />
      </div>
      {!recs.length ? <EmptyState text="No attendance records for this month" testid="attendance-empty" /> : (
        <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
          <Table data-testid="my-attendance-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Check-in</TableHead><TableHead>Check-out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{recs.map((r) => (
              <TableRow key={r.id}><TableCell>{fmtDate(r.date)}</TableCell><TableCell>{fmtTime(r.check_in)}</TableCell><TableCell>{fmtTime(r.check_out)}</TableCell><TableCell>{r.hours}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const DailyRoster = ({ tick }) => {
  const [day, setDay] = useState(todayISO());
  const [emps, setEmps] = useState([]);
  const [recs, setRecs] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [hols, setHols] = useState([]);
  const [offs, setOffs] = useState({});
  useEffect(() => {
    api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status === "active")));
    api.get("/leaves", { params: { status: "approved" } }).then((r) => setLeaves(r.data));
    api.get("/entities").then((r) => setOffs(Object.fromEntries(r.data.map((x) => [x.id, x.weekly_offs || []]))));
  }, []);
  useEffect(() => {
    api.get("/attendance", { params: { day } }).then((r) => setRecs(r.data));
    if (/^\d{4}-\d{2}-\d{2}$/.test(day)) api.get("/holidays", { params: { year: day.slice(0, 4) } }).then((r) => setHols(r.data.filter((h) => h.date === day)));
  }, [day, tick]);
  const byUser = Object.fromEntries(recs.map((r) => [r.user_id, r]));
  const onLeave = (uid) => leaves.some((l) => l.user_id === uid && l.start_date <= day && l.end_date >= day);
  return (
    <div className="space-y-4">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-48 bg-white" data-testid="roster-date-input" />
      <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
        <Table data-testid="roster-table">
          <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Check-in</TableHead><TableHead>Check-out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{emps.map((e) => {
            const r = byUser[e.id];
            const hol = hols.find((h) => h.entity_id === e.entity_id);
            const weekOff = (offs[e.entity_id] || []).includes((new Date(`${day}T00:00:00`).getDay() + 6) % 7);
            const status = r ? r.status : hol ? "holiday" : weekOff ? "week_off" : onLeave(e.id) ? "on_leave" : "absent";
            return (
              <TableRow key={e.id} data-testid={`roster-row-${e.employee_code}`}>
                <TableCell><div className="font-semibold">{e.name}</div><div className="text-xs text-[#6E6E73]">{e.employee_code}</div></TableCell>
                <TableCell>{fmtTime(r?.check_in)}</TableCell><TableCell>{fmtTime(r?.check_out)}</TableCell><TableCell>{r?.hours ?? "—"}</TableCell>
                <TableCell><StatusBadge status={status} /></TableCell>
              </TableRow>
            );
          })}</TableBody>
        </Table>
      </div>
    </div>
  );
};

export default function Attendance() {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);
  return (
    <div data-testid="attendance-page">
      <PageHeader eyebrow="Time" title="Attendance" subtitle="Check-in after 10:00 AM IST is marked late." actions={user.role === "admin" && <AttendanceUpload onDone={bump} />} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6"><CheckInWidget onChange={bump} /><UpcomingHolidays isAdmin={user.role === "admin"} /></div>
        <div className="lg:col-span-2">
          {user.role === "admin" ? (
            <Tabs defaultValue="roster">
              <TabsList className="mb-4"><TabsTrigger value="roster" data-testid="tab-roster">Daily roster</TabsTrigger><TabsTrigger value="mine" data-testid="tab-my-attendance">My attendance</TabsTrigger></TabsList>
              <TabsContent value="roster"><DailyRoster tick={tick} /></TabsContent>
              <TabsContent value="mine"><MyMonth tick={tick} entityId={user.entity_id} /></TabsContent>
            </Tabs>
          ) : <MyMonth tick={tick} entityId={user.entity_id} />}
        </div>
      </div>
    </div>
  );
}
